import { useEffect, useState } from 'react'
import type { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { useReorderLeads } from '@/application/services/lead.service'
import type { Lead } from '@/domains/types'

interface UseLeadReorderOptions {
  investigationId: string
  /** Full, already-sorted (server order) lead list. */
  leads: Lead[]
  onSuccess?: () => void
  onError?: (message: string) => void
}

interface UseLeadReorderResult {
  orderedLeads: Lead[]
  handleDragEnd: (event: DragEndEvent) => Promise<void>
}

/**
 * Owns the optimistic reordering of an investigation's leads.
 *
 * The admin lead list is never paginated, so a drag reorders the whole
 * list — which is also what the backend requires: the complete, exact set
 * of the investigation's lead ids on every reorder call. This hook keeps a
 * local copy of that order so the move shows up immediately, and sends the
 * resulting full id list to the server.
 *
 * On success the new order sticks (until the next server refresh); on
 * failure the local order is rolled back to what it was before the drag.
 */
export function useLeadReorder({
  investigationId,
  leads,
  onSuccess,
  onError,
}: UseLeadReorderOptions): UseLeadReorderResult {
  const [localLeads, setLocalLeads] = useState<Lead[]>(leads)
  const { mutateAsync: reorderLeads } = useReorderLeads(investigationId)

  useEffect(() => {
    setLocalLeads(leads)
  }, [leads])

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const fromIndex = localLeads.findIndex((lead) => lead.id === activeId)
    const toIndex = localLeads.findIndex((lead) => lead.id === overId)
    if (fromIndex === -1 || toIndex === -1) return

    const previous = localLeads
    const next = arrayMove(localLeads, fromIndex, toIndex)
    setLocalLeads(next)

    try {
      await reorderLeads(next.map((lead) => lead.id))
      onSuccess?.()
    } catch (error: unknown) {
      setLocalLeads(previous)
      onError?.(error instanceof Error ? error.message : String(error))
    }
  }

  return { orderedLeads: localLeads, handleDragEnd }
}
